import React, { createContext, useContext, useState } from "react";
import { Theme } from "./types";
import { getCurrentTheme } from "./defaults";
import { ThemeMode } from "../settings/types";

interface ThemeContextType {
  theme: Theme;
  themeMode: ThemeMode;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const ThemeProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  // Always use dark mode
  const [themeMode] = useState<ThemeMode>("dark");
  const [theme] = useState<Theme>(() => getCurrentTheme());

  // No need to update theme based on mode changes since we only have dark mode

  return (
    <ThemeContext.Provider value={{ theme, themeMode }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
};

// Helper hook to get theme-aware colors for geometry
export const useGeometryColors = (appearance: {
  useThemeDefaults: boolean;
  colors?: Record<string, string>;
}) => {
  const { theme } = useTheme();

  if (appearance.useThemeDefaults) {
    return theme.colors.geometry;
  }

  // Return custom colors merged with theme defaults for any unspecified colors
  return {
    lines: {
      default: appearance.colors?.lines ?? theme.colors.geometry.lines.default,
      selected: theme.colors.geometry.lines.selected,
      highlighted: theme.colors.geometry.lines.highlighted,
    },
    edges: {
      default: appearance.colors?.edges ?? theme.colors.geometry.edges.default,
      selected: theme.colors.geometry.edges.selected,
      hidden: theme.colors.geometry.edges.hidden,
    },
    faces: {
      default: appearance.colors?.faces ?? theme.colors.geometry.faces.default,
      selected: theme.colors.geometry.faces.selected,
      highlighted: theme.colors.geometry.faces.highlighted,
    },
    points: {
      default:
        appearance.colors?.points ?? theme.colors.geometry.points.default,
      selected: theme.colors.geometry.points.selected,
      control: theme.colors.geometry.points.control,
    },
  };
};
